<template>
    <div ref="root" class="phone-number-input">
        <div class="phone-number-input__control" :class="{ 'is-focused': isFocused, 'is-open': isCountryPickerOpen }">
            <button type="button" class="phone-number-input__country" @click="openCountryPicker">
                <span :class="['phone-number-input__flag', 'fi', 'fi-' + selectedCountry.flagCode]"></span>
                <span class="phone-number-input__dial">+{{ selectedCountry.dialCode }}</span>
                <span class="phone-number-input__chevron"></span>
            </button>
            <input
                ref="input"
                :value="displayValue"
                class="phone-number-input__field"
                type="tel"
                inputmode="tel"
                autocomplete="tel-national"
                :placeholder="placeholder"
                @input="handleInput"
                @focus="handleFocus"
                @blur="handleBlur"
            >
        </div>

        <div
            v-if="isCountryPickerOpen"
            class="phone-number-input__dropdown"
            :class="{ 'is-below': dropdownPlacement === 'below' }"
        >
            <div class="phone-number-input__search-wrap">
                <input
                    ref="searchInput"
                    v-model.trim="searchQuery"
                    class="phone-number-input__search"
                    type="search"
                    :placeholder="texts.searchPlaceholder"
                >
            </div>
            <div class="phone-number-input__list">
                <button
                    v-for="country in filteredCountries"
                    :key="country.iso2"
                    type="button"
                    class="phone-number-input__option"
                    :class="{ 'is-selected': country.iso2 === selectedCountryCode }"
                    @click="selectCountry(country)"
                >
                    <span :class="['phone-number-input__option-flag', 'fi', 'fi-' + country.flagCode]"></span>
                    <span class="phone-number-input__option-meta">
                        <span class="phone-number-input__option-name">{{ country.displayName }}</span>
                        <span class="phone-number-input__option-code">{{ country.iso2 }}</span>
                    </span>
                    <span class="phone-number-input__option-dial">+{{ country.dialCode }}</span>
                </button>
                <div v-if="!filteredCountries.length" class="phone-number-input__empty">{{ texts.empty }}</div>
            </div>
        </div>
    </div>
</template>

<script>
import { AsYouType, getCountries, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js'
import 'flag-icons/css/flag-icons.min.css'

const ALL_COUNTRY_CODES = getCountries()
const FLAG_CODE_ALIASES = {
    AC: 'sh-ac',
    TA: 'sh-ta',
}

const COPY_MAP = {
    ar: {
        searchPlaceholder: 'ابحث عن الدولة أو رمز الاتصال',
        empty: 'لا توجد نتائج',
    },
    de: {
        searchPlaceholder: 'Land oder Vorwahl suchen',
        empty: 'Keine Ergebnisse',
    },
    en: {
        searchPlaceholder: 'Search country or dial code',
        empty: 'No results found',
    },
    es: {
        searchPlaceholder: 'Buscar país o prefijo',
        empty: 'No se encontraron resultados',
    },
    fr: {
        searchPlaceholder: 'Rechercher un pays ou un indicatif',
        empty: 'Aucun résultat',
    },
    it: {
        searchPlaceholder: 'Cerca paese o prefisso',
        empty: 'Nessun risultato',
    },
    sk: {
        searchPlaceholder: 'Vyhľadajte krajinu alebo predvoľbu',
        empty: 'Žiadne výsledky',
    },
    zh: {
        searchPlaceholder: '搜索国家/地区或区号',
        empty: '未找到结果',
    },
}

export default {
    name: 'PhoneNumberInput',
    props: {
        value: {
            type: String,
            default: '',
        },
        defaultCountryCode: {
            type: String,
            default: '',
        },
        placeholder: {
            type: String,
            default: '',
        },
        locale: {
            type: String,
            default: 'en',
        },
    },
    data() {
        return {
            displayValue: '',
            isFocused: false,
            isCountryPickerOpen: false,
            searchQuery: '',
            selectedCountryCode: this.normalizeCountryCode(this.defaultCountryCode) || 'US',
            dropdownPlacement: 'above',
        }
    },
    computed: {
        normalizedLocale() {
            const locale = String(this.locale || 'en').toLowerCase()

            if (locale.indexOf('zh') === 0) return 'zh'
            if (locale.indexOf('ar') === 0) return 'ar'
            if (locale.indexOf('de') === 0) return 'de'
            if (locale.indexOf('es') === 0) return 'es'
            if (locale.indexOf('fr') === 0) return 'fr'
            if (locale.indexOf('it') === 0) return 'it'
            if (locale.indexOf('sk') === 0) return 'sk'

            return 'en'
        },
        texts() {
            return COPY_MAP[this.normalizedLocale] || COPY_MAP.en
        },
        countries() {
            const locale = this.buildDisplayLocale(this.normalizedLocale)
            const localizedNames = this.buildDisplayNames(locale)
            const englishNames = this.buildDisplayNames('en')

            return ALL_COUNTRY_CODES.map(iso2 => {
                const normalizedIso = iso2.toUpperCase()
                const dialCode = getCountryCallingCode(normalizedIso)
                const displayName = this.getCountryName(localizedNames, normalizedIso)
                const englishName = this.getCountryName(englishNames, normalizedIso)

                return {
                    iso2: normalizedIso,
                    dialCode,
                    displayName,
                    englishName,
                    flagCode: this.getFlagCode(normalizedIso),
                }
            }).sort((left, right) => left.displayName.localeCompare(right.displayName, locale))
        },
        selectedCountry() {
            const matched = this.countries.find(country => country.iso2 === this.selectedCountryCode)
            return matched || this.countries[0]
        },
        filteredCountries() {
            const keyword = String(this.searchQuery || '').trim().toLowerCase()
            if (!keyword) return this.countries

            return this.countries.filter(country => {
                const localizedName = String(country.displayName || '').toLowerCase()
                const englishName = String(country.englishName || '').toLowerCase()
                const iso2 = String(country.iso2 || '').toLowerCase()
                const dial = String(country.dialCode || '')

                return localizedName.indexOf(keyword) !== -1 ||
                    englishName.indexOf(keyword) !== -1 ||
                    iso2.indexOf(keyword) !== -1 ||
                    dial.indexOf(keyword.replace(/^\+/, '')) !== -1 ||
                    ('+' + dial).indexOf(keyword) !== -1
            })
        },
    },
    watch: {
        value(newValue) {
            const formatted = this.formatNationalInput(newValue)
            if (formatted !== this.displayValue) {
                this.displayValue = formatted
            }
            this.emitPhoneState()
        },
        defaultCountryCode(newValue) {
            const normalizedCode = this.normalizeCountryCode(newValue)
            if (!normalizedCode || normalizedCode === this.selectedCountryCode) return

            this.selectedCountryCode = normalizedCode
            this.reformatCurrentValue()
        },
        locale() {
            this.emitPhoneState()
        },
    },
    mounted() {
        this.displayValue = this.formatNationalInput(this.value)
        this.emitPhoneState()
        document.addEventListener('keydown', this.handleDocumentKeydown)
        document.addEventListener('mousedown', this.handleDocumentPointerDown)
        window.addEventListener('resize', this.handleWindowResize)
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.handleDocumentKeydown)
        document.removeEventListener('mousedown', this.handleDocumentPointerDown)
        window.removeEventListener('resize', this.handleWindowResize)
    },
    methods: {
        normalizeCountryCode(code) {
            const normalized = String(code || '').trim().slice(0, 2).toUpperCase()
            return ALL_COUNTRY_CODES.indexOf(normalized) === -1 ? '' : normalized
        },
        buildDisplayLocale(locale) {
            if (locale === 'zh') return 'zh-Hans'
            return locale
        },
        buildDisplayNames(locale) {
            try {
                if (typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function') {
                    return new Intl.DisplayNames([locale], { type: 'region' })
                }
            } catch (error) {
                return null
            }

            return null
        },
        getCountryName(displayNames, iso2) {
            if (displayNames && typeof displayNames.of === 'function') {
                const displayName = displayNames.of(iso2)
                if (displayName) return displayName
            }

            return iso2
        },
        getFlagCode(iso2) {
            return FLAG_CODE_ALIASES[iso2] || iso2.toLowerCase()
        },
        sanitizeInput(value) {
            return String(value || '').replace(/[^\d]/g, '')
        },
        formatNationalInput(value) {
            const digits = this.sanitizeInput(value)
            if (!digits) return ''

            const formatter = new AsYouType(this.selectedCountryCode)
            return formatter.input(digits)
        },
        buildPhoneState() {
            const digits = this.sanitizeInput(this.displayValue)
            const dialCode = this.selectedCountry && this.selectedCountry.dialCode ? String(this.selectedCountry.dialCode) : ''
            let parsedNumber = null

            if (digits) {
                parsedNumber = parsePhoneNumberFromString(digits, this.selectedCountryCode)
            }

            return {
                countryCode: this.selectedCountryCode,
                countryCallingCode: dialCode,
                nationalNumber: parsedNumber ? parsedNumber.nationalNumber : digits,
                phoneNumber: parsedNumber ? parsedNumber.number : '',
                isValid: !!(parsedNumber && parsedNumber.isValid()),
            }
        },
        emitPhoneState() {
            this.$emit('update', this.buildPhoneState())
        },
        emitInputValue() {
            this.$emit('input', this.displayValue)
        },
        reformatCurrentValue() {
            const formatted = this.formatNationalInput(this.displayValue)
            this.displayValue = formatted
            this.emitInputValue()
            this.emitPhoneState()
        },
        handleInput(event) {
            const formatted = this.formatNationalInput(event.target.value)
            this.displayValue = formatted

            if (event.target.value !== formatted) {
                event.target.value = formatted
            }

            this.emitInputValue()
            this.emitPhoneState()
        },
        handleFocus() {
            this.isFocused = true
        },
        handleBlur() {
            this.isFocused = false
            this.emitPhoneState()
        },
        openCountryPicker() {
            this.isCountryPickerOpen = true
            this.updateDropdownPlacement()
            this.$nextTick(() => {
                if (this.$refs.searchInput) {
                    this.$refs.searchInput.focus()
                }
            })
        },
        closeCountryPicker() {
            this.isCountryPickerOpen = false
            this.searchQuery = ''
        },
        selectCountry(country) {
            this.selectedCountryCode = country.iso2
            this.closeCountryPicker()
            this.reformatCurrentValue()
            this.$nextTick(() => {
                if (this.$refs.input) {
                    this.$refs.input.focus()
                }
            })
        },
        updateDropdownPlacement() {
            if (!this.$refs.root || typeof window === 'undefined') return

            const rect = this.$refs.root.getBoundingClientRect()
            const estimatedHeight = 312
            const spaceAbove = rect.top
            const spaceBelow = window.innerHeight - rect.bottom

            this.dropdownPlacement = spaceAbove >= estimatedHeight || spaceAbove >= spaceBelow ? 'above' : 'below'
        },
        handleDocumentKeydown(event) {
            if (event && event.key === 'Escape' && this.isCountryPickerOpen) {
                this.closeCountryPicker()
            }
        },
        handleDocumentPointerDown(event) {
            if (!this.isCountryPickerOpen || !this.$refs.root) return
            if (this.$refs.root.contains(event.target)) return
            this.closeCountryPicker()
        },
        handleWindowResize() {
            if (this.isCountryPickerOpen) {
                this.updateDropdownPlacement()
            }
        },
        blurInput() {
            if (this.$refs.input) {
                this.$refs.input.blur()
            }
        },
    },
}
</script>

<style lang="scss">
.phone-number-input {
    position: relative;
    width: 100%;
}

.phone-number-input__control {
    display: flex;
    align-items: stretch;
    gap: 9px;
}

.phone-number-input__country,
.phone-number-input__field {
    height: 46px;
    border: 1px solid #e6e6e6;
    border-radius: 5px;
    background: #fff;
    box-shadow: 0px 1px 2.8px 0px rgba(0, 0, 0, 0.05);
    transition: border-color 0.12s ease;
}

.phone-number-input__country {
    width: 103px;
    min-width: 103px;
    padding: 0 10px;
    display: flex;
    align-items: center;
    gap: 7px;
    color: #333333;
    font-size: 14px;
    font-weight: 500;
}

.phone-number-input__field {
    flex: 1;
    min-width: 0;
    padding: 0 16px;
    font-size: 14px;
    font-weight: 400;
    color: #333333;
    outline: none;

    &::placeholder {
        color: #c6c6c6;
        font-weight: 400;
    }
}

.phone-number-input__control.is-focused .phone-number-input__field {
    border-color: #3183ff;
}

.phone-number-input__control.is-open .phone-number-input__country {
    border-color: #3183ff;
}

.phone-number-input__flag {
    width: 22px;
    height: 16px;
    flex: 0 0 auto;
    border-radius: 2px;
    box-shadow: 0 0 1px rgba(0, 0, 0, 0.25);
}

.phone-number-input__dial {
    flex: 1;
    text-align: center;
    white-space: nowrap;
}

.phone-number-input__chevron {
    width: 8px;
    height: 8px;
    border-right: 2px solid #333333;
    border-bottom: 2px solid #333333;
    transform: rotate(45deg) translateY(-1px);
    flex: 0 0 auto;
}

.phone-number-input__dropdown {
    position: absolute;
    left: 0;
    right: 0;
    bottom: calc(100% + 8px);
    z-index: 80;
    background: #ffffff;
    border: 1px solid #e6e6e6;
    border-radius: 5px;
    box-shadow: 0px 3px 32px rgba(0, 0, 0, 0.12);
    overflow: hidden;
}

.phone-number-input__dropdown.is-below {
    top: calc(100% + 8px);
    bottom: auto;
}

.phone-number-input__search-wrap {
    padding: 10px;
    border-bottom: 1px solid #f0f0f0;
    background: #ffffff;
}

.phone-number-input__search {
    width: 100%;
    height: 46px;
    border: 1px solid #e6e6e6;
    border-radius: 5px;
    background: #ffffff;
    padding: 0 14px;
    color: #333333;
    font-size: 14px;
    outline: none;

    &:focus {
        border-color: #3183ff;
    }

    &::placeholder {
        color: #c6c6c6;
    }
}

.phone-number-input__list {
    overflow-y: auto;
    max-height: min(42vh, 315px);
    padding: 6px 0;
}

.phone-number-input__option {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    min-height: 58px;
    border-radius: 0;
    background: transparent;
    border: 0;
    text-align: left;
    color: #333333;
}

.phone-number-input__option.is-selected {
    background: rgba(49, 131, 255, 0.1);
}

.phone-number-input__option-flag {
    width: 22px;
    height: 16px;
    flex: 0 0 auto;
    border-radius: 2px;
    box-shadow: 0 0 1px rgba(0, 0, 0, 0.25);
}

.phone-number-input__option-meta {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.phone-number-input__option-name {
    font-size: 14px;
    font-weight: 500;
    color: #333333;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.phone-number-input__option-code,
.phone-number-input__option-dial {
    font-size: 14px;
    color: #7a7a7a;
}

.phone-number-input__empty {
    padding: 24px 12px 28px;
    text-align: center;
    font-size: 14px;
    color: #7a7a7a;
}
</style>
